import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { GuideStepModel } from '@assetaccountant/core/models/onboarding/guide-step-model';
import { OnboardingGuideStatus } from '@assetaccountant/core/models/onboarding/onboarding-guide-status';
import { OnboardingGuideType } from '@assetaccountant/core/models/onboarding/onboarding-guide-type';
import { OnboardingRegisterGuideStepStatus } from '@assetaccountant/core/models/onboarding/onboarding-register-guide-step-status';
import { OnboardingRegisterModel } from '@assetaccountant/core/models/onboarding/onboarding-register-model';
import { Register } from '@assetaccountant/core/models/register';
import { PeriodData, PeriodParams } from '@assetaccountant/core/resolvers/period.resolver';
import { OnboardingService } from '@assetaccountant/core/services/onboarding.service';
import { ActiveComponentService } from '@assetaccountant/core/services/title.service';
import { TrackingService } from '@assetaccountant/core/services/tracking.service';
import { UserService } from '@assetaccountant/core/services/user.service';
import {
  getChildRoute,
  TypedActivatedRouteSnapshot,
} from '@assetaccountant/core/utils/route-utils';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Component({
  selector: 'app-guide-banner',
  templateUrl: './guide-banner.component.html',
  styleUrls: ['./guide-banner.component.scss'],
  standalone: false,
})
export class GuideBannerComponent implements OnInit {
  readonly OnboardingRegisterGuideStepStatus = OnboardingRegisterGuideStepStatus;

  currentComponentName$ = new BehaviorSubject<string | undefined>(undefined);
  currentGuideType = OnboardingGuideType.SetupGuide;
  currentRegister$ = new BehaviorSubject<Register | null>(null);
  onboardingRegister?: OnboardingRegisterModel;
  showExpandedGuide$ = this.onboardingService?.showExpandedGuide;
  showGuide$ = combineLatest([this.onboardingService.guideStatus$, this.currentRegister$]).pipe(
    map(
      ([status, currentRegister]) =>
        status === OnboardingGuideStatus.Show && !!currentRegister && !currentRegister.isDemo,
    ),
  );

  get currentComponentName() {
    return this.currentComponentName$.value;
  }

  get currentStep(): GuideStepModel | null {
    return (
      this.setupGuideSteps?.find(
        s =>
          s.status === OnboardingRegisterGuideStepStatus.NotStarted ||
          s.status === OnboardingRegisterGuideStepStatus.Next,
      ) || null
    );
  }

  get isLastStep() {
    return this.currentStep?.stepId === this.setupGuideSteps?.length;
  }

  get isOnCurrentStepPage() {
    return this.currentComponentName === this.currentStep?.componentName;
  }

  get progressPercent(): number {
    return (
      (this.currentStep &&
        this.setupGuideSteps &&
        (this.setupGuideSteps.findIndex(s => s.stepId === this.currentStep?.stepId) /
          (this.setupGuideSteps.length - 1)) *
          100) ||
      0
    );
  }

  get setupGuideSteps() {
    return this.onboardingRegister?.setupGuide?.steps;
  }

  constructor(
    public onboardingService: OnboardingService,
    private router: Router,
    private trackingService: TrackingService,
    private activeComponentService: ActiveComponentService,
    private userService: UserService,
  ) {}

  ngOnInit() {
    this.router.events
      .pipe(filter((e: any): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        const { register } = getChildRoute(
          this.router.routerState.snapshot.root as TypedActivatedRouteSnapshot<
            PeriodParams,
            PeriodData
          >,
        ).data;

        if (this.currentRegister$.value !== register) {
          this.currentRegister$.next(register);
        }

        this.onboardingService.registerOnboarding.subscribe(result => {
          this.onboardingRegister = result;
        });

        if (register?.hasPermission) {
          this.onboardingService.getRegisterOnboarding(register.id).subscribe();
        }
      });

    this.activeComponentService.activeComponent$.subscribe(result => {
      this.currentComponentName$.next(result?.componentName);
    });
  }

  onAction() {
    if (this.currentStep?.route) {
      this.router.navigateByUrl(this.currentStep.route);

      this.trackingService.trackEvent(
        `${OnboardingGuideType[this.currentGuideType]}-start-step-${this.currentStep.stepId} ${
          this.currentStep.name
        }`,
        this.currentStep.name,
      );
    }
  }

  onClose() {
    this.trackingService.trackEvent('Hide guides');
    this.userService.updateUserSettings({ showGuides: false }).subscribe();
  }

  onDone() {
    if (this.currentStep) {
      const { stepId, guideType } = this.currentStep;
      this.onboardingService.updateRegisterGuideStepStatus(
        stepId,
        OnboardingRegisterGuideStepStatus.Completed,
        guideType,
      );

      this.trackingService.trackEvent(`${OnboardingGuideType[this.currentGuideType]}-completed`);
    }
  }

  onSkip() {
    if (this.currentStep && this.currentStep.isSkippable) {
      const { stepId, guideType } = this.currentStep;
      this.onboardingService.updateRegisterGuideStepStatus(
        stepId,
        OnboardingRegisterGuideStepStatus.Skipped,
        guideType,
      );

      this.trackingService.trackEvent(
        `${OnboardingGuideType[this.currentGuideType]}-skip-step-${this.currentStep.stepId}`,
        this.currentStep.name,
      );
    }
  }
}
